import { useState } from "react";
import { LupaDeAmpliar } from "../../components/Ampliar.js";
import { VisorDeImagem } from "../../components/VisorDeImagem.js";
import { cx } from "../../components/ui.js";
import type { SaidaRota } from "../../lib/trpc.js";

type Capa = SaidaRota["bookCovers"]["list"][number];

const nomeDaCapa = (c: Capa) => c.label ?? c.originalName;

/**
 * "Capa do livro": opcional, e com ela as pessoas aparecem segurando o livro. As capas são maiores que
 * antes (96 × 144 px, e não 72 × 96), a escolhida leva a borda da brasa, e cada uma tem a lupa, que a
 * abre no visor com a ação "Usar esta capa". A fila rola na horizontal, dentro dela: a página não.
 */
export function CapasDoLivro({
  capas,
  escolhida,
  aoEscolher,
}: {
  capas: readonly Capa[];
  escolhida: number | null;
  aoEscolher: (id: number | null) => void;
}) {
  const [ampliando, setAmpliando] = useState<number | null>(null);
  const emVisor = ampliando !== null ? capas.find((c) => c.id === ampliando) : undefined;

  return (
    <div>
      <h2 className="mb-1 text-[0.9375rem] font-medium text-texto">Capa do livro</h2>
      <p className="mb-2 text-[0.8125rem] text-texto-suave">
        Opcional. Com a capa, as pessoas aparecem segurando o livro.
      </p>
      <ul className="flex snap-x snap-proximity gap-2 overflow-x-auto scroll-smooth pb-2 motion-reduce:scroll-auto">
        <li className="shrink-0 snap-start">
          <button
            type="button"
            onClick={() => aoEscolher(null)}
            aria-pressed={escolhida === null}
            className={cx(
              "grid h-36 w-24 place-items-center rounded-peca border-2 bg-superficie px-2 text-center text-[0.8125rem] leading-tight text-texto-suave",
              escolhida === null ? "border-brasa" : "border-borda hover:border-borda-forte",
            )}
          >
            Sem capa
          </button>
        </li>
        {capas.map((c) => (
          <li key={c.id} className="relative shrink-0 snap-start">
            <button
              type="button"
              onClick={() => aoEscolher(c.id)}
              aria-pressed={escolhida === c.id}
              title={nomeDaCapa(c)}
              className={cx(
                "block h-36 w-24 overflow-hidden rounded-peca border-2 bg-superficie",
                escolhida === c.id ? "border-brasa" : "border-transparent hover:border-borda-forte",
              )}
            >
              <img
                src={c.url}
                alt={nomeDaCapa(c)}
                loading="lazy"
                className="size-full object-contain"
              />
            </button>
            <LupaDeAmpliar
              rotulo={`Ampliar a capa ${nomeDaCapa(c)}`}
              aoClicar={() => setAmpliando(c.id)}
              className="absolute top-0 right-0"
            />
          </li>
        ))}
      </ul>

      {emVisor && (
        <VisorDeImagem
          titulo={`Capa do livro: ${nomeDaCapa(emVisor)}`}
          alt={`Capa do livro ${nomeDaCapa(emVisor)}`}
          // O visor mede a capa: o servidor não manda as dimensões.
          versoes={[
            { id: "capa", rotulo: nomeDaCapa(emVisor), url: emVisor.url, previa: emVisor.url },
          ]}
          acao={{ rotulo: "Usar esta capa", aoAcionar: () => aoEscolher(emVisor.id) }}
          aoFechar={() => setAmpliando(null)}
        />
      )}
    </div>
  );
}
